import type { QueryClient } from '@tanstack/react-query'

import { clearPendingSignupState } from '../contexts/auth-flow-storage'
import { setAccessToken } from '../dal/api-config'
import { resetAnalytics } from '../services/analytics'
import { setStoredIsAuthenticated } from './auth-session-storage'
import { clearUserContext } from './sentry'
import { deleteItem } from './storage'
import {
  buildScopedStorageKey,
  getActiveStorageScopeUserId,
  setActiveStorageScopeUserId,
} from './storage-scope'

interface ClearLocalSessionStateParams {
  authUserId: string | null
  queryClient: Pick<QueryClient, 'clear'>
  resetRevenueCat?: () => Promise<void>
}

export async function clearLocalSessionState({
  authUserId,
  queryClient,
  resetRevenueCat,
}: ClearLocalSessionStateParams): Promise<void> {
  const activeScopeUserId = await getActiveStorageScopeUserId()
  const userIdsToClear = [authUserId, activeScopeUserId].filter(
    (value, index, values): value is string =>
      typeof value === 'string' &&
      value.length > 0 &&
      values.indexOf(value) === index,
  )

  clearUserContext()
  resetAnalytics()
  setAccessToken(null)
  queryClient.clear()

  const authStorageKeysToClear = [
    'auth-storage',
    ...userIdsToClear.map((userId) =>
      buildScopedStorageKey('auth-storage', userId),
    ),
  ].filter((value, index, values) => values.indexOf(value) === index)
  const legacyPendingPurchaseRedirectKeys = userIdsToClear.map(
    (userId) => `pendingPurchaseRedirect-${userId}`,
  )

  await Promise.all([
    await clearPendingSignupState(),
    deleteItem('active_week'),
    ...authStorageKeysToClear.map((storageKey) =>
      deleteItem(storageKey, { scoped: false }),
    ),
    ...legacyPendingPurchaseRedirectKeys.map((storageKey) =>
      deleteItem(storageKey, { scoped: false }),
    ),
    ...(resetRevenueCat != null ? [resetRevenueCat()] : []),
    ...userIdsToClear.map((userId) =>
      setStoredIsAuthenticated({ isAuthenticated: false, userId }),
    ),
  ])

  await setActiveStorageScopeUserId(null)
}
